import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ADVISOR_ROUTES } from '../../routes/routeConstants';
import { AccountsCard } from '../../containers/create-client-step-flow2/AccountsCard';
import { AccountsInlineTable } from '../../containers/create-client-step-flow2/AccountsInlineTable';
import { PersonalInfoCard } from '../../containers/create-client-step-flow2/PersonalInfoCard';
import { PersonalInfoCardInline } from '../../containers/create-client-step-flow2/PersonalInfoCardInline';
import { IncomeCard } from '../../containers/create-client-step-flow2/IncomeCard';
import { PersonalInfoAndRelationshiopDrawerFF } from '../../containers/create-client-step/PersonalInfoAndRelationshiopDrawerFF';
import { Tabs, TabVariant } from '../../components/molecules/Tabs/Tabs';
import { useAppSelector } from '../../common/hooks';
import { isStageDisabled } from '../../common/helper';
import { CLIENT_PROFILE_STAGE_KEYS, FEATURE_NAMES } from '../../common/constants';
import { checkFFEnabled } from '../../utils';

export const CreateClientStep = ({
    tabIndex,
    setTabIndex,
    // Legacy drawer-based flow props
    openClientDrawer,
    setOpenClientDrawer,
    isRelationship,
    setIsRelationship,
    // Inline-editable flow props
    profileDraftActive,
    profileDraftBlocks,
    planHasDetailsBeyondProfile,
    requestProfileLeave,
    requestAccountsLeave,
    editingRowKey,
    onEditingRowKeyChange,
    isAddingNew,
    onAddCoClient,
    onCancelAdd
}: any) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const adviceClient = useAppSelector((state) => state.adviceClient);

    const isAccountIncomeMappingEnabled = checkFFEnabled(FEATURE_NAMES['ACCOUNT_INCOME_MAPPING_FE_AEX-3270']);

    // Accounts tab hosts IncomeCard (stage[2]) and AccountsCard (stage[3]). Disable the tab
    // only when the add-buttons in both cards are disabled, mirroring their isStageDisabled checks.
    const isAccountsTabDisabled =
        isStageDisabled(adviceClient, CLIENT_PROFILE_STAGE_KEYS[2]) &&
        isStageDisabled(adviceClient, CLIENT_PROFILE_STAGE_KEYS[3]);

    useEffect(() => {
        // Don't navigate if logout is in progress
        if (sessionStorage.getItem('isLoggingOut')) return;

        console.log(
            'CreateClientStepFF mounted, navigating to CLIENT_PROFILE if not already there',
            sessionStorage.getItem('isLoggingOut')
        );
        if (location.pathname !== ADVISOR_ROUTES.CLIENT_PROFILE)
            navigate(ADVISOR_ROUTES.CLIENT_PROFILE, { replace: true });
    }, []);

    const tabPanels = [
        {
            enabled: true,
            title: t('PROFILE_TAB_LABEL'),
            hidden: false,
            children: isAccountIncomeMappingEnabled ? (
                <PersonalInfoCardInline
                    editingRowKey={editingRowKey}
                    onEditingRowKeyChange={onEditingRowKeyChange}
                    isAddingNew={isAddingNew}
                    onAddCoClient={onAddCoClient}
                    onCancelAdd={onCancelAdd}
                />
            ) : (
                <PersonalInfoCard setOpenDrawer={setOpenClientDrawer} setIsRelationship={setIsRelationship} />
            )
        },
        {
            enabled: !isAccountsTabDisabled && !(isAccountIncomeMappingEnabled && profileDraftBlocks),
            title: t('ACCOUNTS_LABEL_TEXT'),
            hidden: false,
            children: isAccountIncomeMappingEnabled ? (
                <AccountsInlineTable />
            ) : (
                <>
                    <IncomeCard />
                    <AccountsCard />
                </>
            )
        }
    ];

    const handleTabChange = (_event: React.SyntheticEvent, value: string) => {
        if (isAccountIncomeMappingEnabled && profileDraftActive) {
            // Brand-new, profile-only plan: keep blocking tab switches while adding/editing.
            if (!planHasDetailsBeyondProfile) return;
            // Plan with other details: confirm (Stay/Leave) before leaving an in-progress add/edit.
            // requestProfileLeave returns true when it opened the modal (navigation deferred).
            if (requestProfileLeave?.(() => setTabIndex(value))) return;
        }
        // Leaving the Accounts tab (index '1') with an in-progress account/income edit: confirm
        // (Stay/Leave) before switching. requestAccountsLeave returns true when it opened the modal.
        if (isAccountIncomeMappingEnabled && tabIndex === '1' && requestAccountsLeave?.(() => setTabIndex(value))) {
            return;
        }
        setTabIndex(value);
    };

    return (
        <>
            <Tabs tabPanels={tabPanels} value={tabIndex} onChange={handleTabChange} variant={TabVariant.ENCLOSED_TAB} />
            {!isAccountIncomeMappingEnabled && openClientDrawer && (
                <PersonalInfoAndRelationshiopDrawerFF
                    setOpenDrawer={setOpenClientDrawer}
                    openDrawer={openClientDrawer}
                    isRelationship={isRelationship}
                    setIsRelationship={setIsRelationship}
                />
            )}
        </>
    );
};
